import React, { useEffect, useRef, useState } from "react";
import { Box, TextField, Tooltip } from "@mui/material";

import { AiModelRate } from "./aiModelRates";
import { DebouncedCommit, createDebouncedCommit } from "./debouncedCommit";
import { CostResult } from "./aiModelCost";

/**
 * ช่องราคาในตาราง — 1 ช่องต่อ variant (video/image/call) หรือ 2 ช่อง (token เข้า/ออก)
 * `PriceInput`: พิมพ์แล้วเห็นผลเอง แต่ **commit เข้า store แบบ debounce** (ค่าผิด → ขอบแดง ไม่ commit)
 *   เพราะ 1 commit = เขียน localStorage + คำนวณ aggregate/กราฟของ dashboard ใหม่ทั้งชุด (records เป็นหมื่นแถวได้)
 *   ถ้า commit ทุก keystroke ช่องกรอกจะหนืดและตัวอักษรหาย · blur/Enter = commit ทันทีไม่ต้องรอ debounce
 * `AiRatePriceCell`: แสดงราคาที่ใช้ + badge อย่างเดียว (modal AI Usage / แท็บ ราย Model) — แก้ราคาได้ที่แท็บ ราคา Model เท่านั้น
 * ราคาที่แก้บันทึกใน localStorage ผ่าน useAiModelRates
 */

// ─── Price input ─────────────────────────────────────────────────────────────

type PriceInputProps = {
  id: string;
  value: number | undefined; // undefined = ยังไม่ได้ตั้งราคา
  overridden: boolean;
  missing: boolean;
  placeholder?: string;
  onCommit: (usd: number) => void;
};

/** หน่วงก่อน commit ราคาเข้า store (ms) — สั้นพอให้รู้สึกว่าอัปเดตทันที แต่ไม่คำนวณใหม่ทุกตัวอักษร */
export const PRICE_COMMIT_DEBOUNCE_MS = 350;

const toDraft = (v: number | undefined): string => (v === undefined ? "" : String(v));

export const PriceInput: React.FC<PriceInputProps> = ({ id, value, overridden, missing, placeholder, onCommit }) => {
  const [draft, setDraft] = useState(toDraft(value));
  const [invalid, setInvalid] = useState(false);

  // onCommit เป็น lambda ใหม่ทุก render → เก็บใน ref เพื่อให้ตัว debounce ไม่ถือ closure เก่า
  const onCommitRef = useRef(onCommit);
  useEffect(() => {
    onCommitRef.current = onCommit;
  }, [onCommit]);

  const commitRef = useRef<DebouncedCommit<number> | null>(null);
  if (!commitRef.current) {
    commitRef.current = createDebouncedCommit<number>((usd) => onCommitRef.current(usd), PRICE_COMMIT_DEBOUNCE_MS);
  }
  const debounced = commitRef.current;

  // unmount (ปิด modal / สลับแท็บใน MeGen Cost) ระหว่างที่ยังรอ debounce → ต้องไม่ทิ้งค่าที่ผู้ใช้พิมพ์
  // ครอบเฉพาะ unmount เท่านั้น — reload/ปิดแท็บเบราว์เซอร์ภายใน PRICE_COMMIT_DEBOUNCE_MS โดยไม่ blur ยังเสียค่าที่ค้างอยู่
  useEffect(() => () => debounced.flush(), [debounced]);

  // resync เมื่อค่าภายนอกเปลี่ยน (reset / แก้จากแท็บอื่น) — ไม่ทับตอนกำลังพิมพ์ เช่น "0." (ค่าเท่ากัน)
  // ถ้าค่าภายนอกเปลี่ยนจริง ต้องยกเลิก commit ที่ค้างรอ debounce ด้วย ไม่งั้นมันจะเขียนทับค่าใหม่ทีหลัง
  useEffect(() => {
    const draftNum = draft.trim() === "" ? undefined : Number(draft);
    if (draftNum !== value) {
      debounced.cancel();
      setDraft(toDraft(value));
      setInvalid(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const borderColor = invalid ? "#dc2626" : overridden ? "#db2777" : missing ? "#f59e0b" : "#e2e8f0";

  return (
    <TextField
      id={id}
      size="small"
      type="number"
      value={draft}
      placeholder={missing ? placeholder || "ตั้งราคา" : undefined}
      onChange={(e) => {
        const s = e.target.value;
        setDraft(s);
        if (s.trim() === "") {
          // ล้างช่องแล้วต้องทิ้งค่าที่ push ค้างไว้ด้วย ไม่งั้นมัน commit ทีหลัง → effect resync เห็นค่าไม่ตรง
          // แล้วเติมตัวเลขกลับเข้าช่องที่ผู้ใช้เพิ่งล้าง
          debounced.cancel();
          // `<input type="number">` รายงาน value = "" ทั้งตอน "ล้างช่องจริง" และตอนพิมพ์ค่าที่ยังไม่เป็นเลข
          // (เช่น "5e" ระหว่างทางไป "5e3") — แยกสองเคสด้วย badInput
          // ล้างจริง = ไม่ขึ้นขอบแดง · พิมพ์ค้างไว้ = ขอบแดงบอกว่ายังไม่รับค่า ไม่ใช่เงียบแล้วเด้งค่าเก่ากลับตอน blur
          setInvalid(e.target.validity.badInput);
          return;
        }
        const n = Number(s);
        if (Number.isFinite(n) && n >= 0) {
          setInvalid(false);
          debounced.push(n);
        } else {
          debounced.cancel(); // ค่าผิดรูป = ไม่ commit และต้องทิ้งค่าที่ค้างจากตัวอักษรก่อนหน้าด้วย
          setInvalid(true);
        }
      }}
      onBlur={() => {
        debounced.flush();
        if (draft.trim() === "" || invalid) {
          setDraft(toDraft(value));
          setInvalid(false);
        }
      }}
      onKeyDown={(e) => {
        if (e.key === "ArrowUp" || e.key === "ArrowDown") e.preventDefault(); // กันปัดราคาเป็น step (เช่น 0.0375 → 1.0375)
        if (e.key === "Enter") debounced.flush();
      }}
      onWheel={(e) => (e.target as HTMLElement).blur()}
      inputProps={{
        min: 0,
        step: "any", // ราคาเช่น 0.0375 ต้องไม่ถูกมองว่า step mismatch
        style: {
          width: 64,
          textAlign: "right",
          fontSize: "0.75rem",
          padding: "3px 6px",
          fontVariantNumeric: "tabular-nums",
          fontWeight: overridden ? 700 : 400,
          color: overridden ? "#db2777" : "#1e293b",
        },
      }}
      sx={{
        "& .MuiOutlinedInput-root": { backgroundColor: "white", borderRadius: "0.375rem" },
        "& .MuiOutlinedInput-notchedOutline": { borderColor: `${borderColor} !important` },
        "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": { WebkitAppearance: "none", margin: 0 },
        "& input[type=number]": { MozAppearance: "textfield" },
      }}
    />
  );
};

// ─── Badges ──────────────────────────────────────────────────────────────────

const badgeStyle = (bg: string, color: string, border: string): React.CSSProperties => ({
  display: "inline-block",
  padding: "0 6px",
  borderRadius: "9999px",
  fontSize: "0.65rem",
  fontWeight: 600,
  lineHeight: "16px",
  backgroundColor: bg,
  color,
  border: `1px solid ${border}`,
  whiteSpace: "nowrap",
});

export const MissingRateBadge: React.FC = () => (
  <span style={badgeStyle("#fef3c7", "#b45309", "#fcd34d")}>ยังไม่ได้ตั้งราคา</span>
);

// gpu_hour แต่ log ไม่มี duration_ms → คิดค่า GPU ไม่ได้
export const QuantityUnknownBadge: React.FC = () => (
  <Tooltip title="log ไม่มีเวลาเครื่อง (duration_ms) ของ call นี้ → คิดค่า GPU ไม่ได้ (นับเป็น $0)">
    <span style={{ ...badgeStyle("#f1f5f9", "#64748b", "#e2e8f0"), cursor: "help" }}>ไม่มีเวลาเครื่อง</span>
  </Tooltip>
);

export const EstimatedBadge: React.FC<{ reasons: string[] }> = ({ reasons }) => (
  <Tooltip
    title={
      <Box component="ul" sx={{ m: 0, pl: 2, fontSize: "0.72rem" }}>
        {reasons.map((r) => (
          <li key={r}>{r}</li>
        ))}
      </Box>
    }
  >
    <span style={{ ...badgeStyle("#f1f5f9", "#64748b", "#e2e8f0"), cursor: "help" }}>ประมาณการ</span>
  </Tooltip>
);

// ─── Cell ────────────────────────────────────────────────────────────────────

export type AiRatePriceCellProps = {
  idPrefix: string; // id ของ span ราคา = `${idPrefix}-rate` (ใช้กับ e2e)
  rate: AiModelRate; // rate ที่ resolve แล้ว (รวม override)
  result: CostResult; // ผลของแถว: variant / unitPrice / missingRate / reasons
  fallbackFrom?: string; // ยืมราคาจาก model นี้ (snapshot / ค่าเริ่มต้นของ provider)
  isOverridden: (variant: string) => boolean;
  readOnlyHint?: React.ReactNode; // ข้อความ/ลิงก์เพิ่มเติมท้ายราคา
};

// variant ที่ราคาในช่องนี้มาจาก — token = เข้า/ออก, อื่น ๆ = variant ของแถว
const variantsOf = (rate: AiModelRate, variant: string): string[] =>
  rate.unit === "token_1m" ? ["in", "out"] : [variant];

const AiRatePriceCell: React.FC<AiRatePriceCellProps> = (props) => {
  const { rate, result, fallbackFrom } = props;

  const anyOverridden = variantsOf(rate, result.variant).some((v) => props.isOverridden(v));
  const idOf = (variant: string) => `${props.idPrefix}-${variant}`.replace(/[^a-zA-Z0-9_-]+/g, "-");

  const badges = (result.missingRate || result.quantityUnknown || result.estimated || fallbackFrom) && (
    <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 0.5 }}>
      {result.missingRate && <MissingRateBadge />}
      {result.quantityUnknown && <QuantityUnknownBadge />}
      {result.estimated && <EstimatedBadge reasons={result.reasons} />}
      {fallbackFrom && (
        <span style={{ fontSize: "0.65rem", color: "#94a3b8", whiteSpace: "nowrap" }}>ใช้ราคา {fallbackFrom}</span>
      )}
    </Box>
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
      <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 0.75 }}>
        <span
          id={idOf("rate")}
          style={{ fontSize: "0.75rem", fontWeight: 600, color: anyOverridden ? "#db2777" : "#1e293b", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}
        >
          {result.rateLabel}
        </span>
        {anyOverridden && (
          <Tooltip title="ราคานี้ถูกแก้ในแท็บ ราคา Model (บันทึกใน browser นี้)">
            <span style={badgeStyle("#fdf2f8", "#be185d", "#f9a8d4")}>แก้ไขแล้ว</span>
          </Tooltip>
        )}
        {rate.note && (
          <Tooltip title={rate.note}>
            <span style={{ fontSize: "0.7rem", color: "#94a3b8", cursor: "help" }}>ⓘ</span>
          </Tooltip>
        )}
        {props.readOnlyHint}
      </Box>
      {badges}
    </Box>
  );
};

export default AiRatePriceCell;
